// apps/web/src/features/readingnote/pages/ReadingNoteComposePage.tsx
import React, { useEffect, useState } from "react";
import { usePageScale } from "@/ui/usePageScale";
import { PageShell } from "@/ui/PageShell";
import { HEADER, withBase } from "@/ui/Header";
import { me, reviewCreate, bookById, fetchReviewKeywords, RATING_LABELS, isLoggedIn } from "@/lib/api";
import { notify } from "@/ui/notify";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { SPC_MODEL, getDeviceModel } from "@/lib/androidBridge";

const BASE = {
    W: 1264,
    H: 1424,
    MARGIN_X: 108,
    BLOCK_W: 1048,
    TEXTAREA_H: 230,
    STAR_SIZE: 120,
    GAP: 50,
    BTN_W: 326,
    BTN_H: 124,
};
const BTN_GAP = (BASE.BLOCK_W - 3 * BASE.BTN_W) / 2;

function useScale() {
    const { px, sfpx } = usePageScale({ baseW: BASE.W, max: 1.2 });
    return { spx: px, sfpx };
}

const ICONS = { back: withBase("icons/prev1.svg") };

function StarIcon({ filled, size }: { filled: boolean; size: number }) {
    const fill = filled ? "#111" : "#D1D5DB";   // ✅ 비선택도 회색 채움
    const stroke = filled ? "#111" : "#D1D5DB"; // ✅ 테두리도 회색(취향)

    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
            <path
                d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                fill={fill}
                stroke={stroke}
                strokeWidth={2.6}
                strokeLinejoin="round"
            />
        </svg>
    );
}


export default function ReviewNoteComposePage() {
    const { spx, sfpx } = useScale();
    const nav = useNavigate();
    const isSpc = React.useMemo(() => getDeviceModel() === SPC_MODEL, []);
    const location = useLocation();
    const [sp] = useSearchParams();
    const bookId = Number(sp.get("bookId") || 0);

    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [rating, setRating] = useState(0);
    const [keywords, setKeywords] = useState<number[]>([]);
    const [reviewKeywords, setReviewKeywords] = useState<{ id: number; keyword: string }[]>([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchReviewKeywords().then(setReviewKeywords).catch(console.error);
    }, []);

    // 로그인 체크
    useEffect(() => {
        if (!isLoggedIn()) {
            nav("/auth/need-login", { state: { from: location.pathname + location.search, featureName: "리뷰" }, replace: true });
        }
    }, [nav, location]);

    useEffect(() => {
        (async () => {
            try { await me(); }
            catch { nav(`/auth/login?next=${encodeURIComponent(location.pathname + location.search)}`); return; }
            if (!Number.isFinite(bookId) || bookId <= 0) { notify("책 정보가 없습니다. 다시 시도해주세요."); nav("/search"); return; }

            try {
                const b: any = await bookById(bookId);
                const t = b?.title || b?.title_plain || b?.book?.title || b?.book?.title_plain || "";
                setTitle(t);
            } catch { /* optional */ }
        })();
    }, [bookId, nav]);

    const toggleKeyword = (id: number) => {
        setKeywords(prev => {
            if (prev.includes(id)) return prev.filter(k => k !== id);
            if (prev.length >= 5) { notify("키워드는 최대 5개까지 선택할 수 있어요."); return prev; }
            return [...prev, id];
        });
    };

    const submit = async () => {
        if (submitting) return;
        if (rating === 0) { notify("별점을 선택해주세요."); return; }
        const text = content.trim();
        if (text.length < 10 || text.length > 100) { notify("한 줄 평은 10~100자로 작성해주세요."); return; }

        setSubmitting(true);
        try {
            await reviewCreate({ book_id: bookId, content: text, rating, keywords });
            nav(`/books/${bookId}/reviews`, { replace: true });
        } catch (e: any) {
            const code = e?.code || e?.json?.error || "";
            if (code === "ALREADY_EXISTS") {
                notify("이미 이 책에 리뷰를 작성하셨습니다.");
                nav(`/books/${bookId}/reviews`, { replace: true });
            } else {
                notify(e?.message || "리뷰 등록에 실패했습니다.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageShell
            title="리뷰 작성"
            onBack={() => nav(-1)}
            backIcon={ICONS.back}
            width={spx(BASE.W)}
        >
            <div style={{
                gridArea: "body",
                padding: `0 ${spx(BASE.MARGIN_X)} 0`,
                display: "flex",
                flexDirection: "column",
                gap: spx(BASE.GAP),
            }}>
                {/* 1. 책 제목 */}
                <div style={{ fontWeight: FW.extraBold, fontSize: sfpx(40), lineHeight: spx(52), color: COLOR.text }}>
                    {title || "제목 없음"}
                </div>

                {/* 2. 한 줄 평 */}
                <div style={{ display: "flex", flexDirection: "column", gap: spx(8) }}>
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="리뷰를 작성해주세요"
                        maxLength={100}
                        style={{
                            width: spx(BASE.BLOCK_W),
                            height: spx(isSpc ? 360 : BASE.TEXTAREA_H),
                            padding: spx(20),
                            fontSize: sfpx(32),
                            lineHeight: 1.5,
                            border: BORDER.dark,
                            borderRadius: RADIUS.md,
                            background: COLOR.white,
                            boxSizing: "border-box",
                            resize: "none",
                            color: COLOR.text,
                            outline: "none",
                        }}
                    />
                    <div style={{ fontSize: sfpx(24), color: "#999", textAlign: "right", width: spx(BASE.BLOCK_W) }}>
                        {content.trim().length}/100
                    </div>
                </div>

                {/* 3. 별점등록 */}
                <div style={{ display: "flex", flexDirection: "column", gap: spx(12) }}>
                    <div style={{ fontWeight: FW.bold, fontSize: sfpx(32), color: COLOR.text }}>별점등록</div>
                    <div style={{ display: "flex", gap: spx(16), alignItems: "center", justifyContent: "center" }}>
                        {[1, 2, 3, 4, 5].map(n => {
                            const filled = n <= rating;
                            return (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setRating(n)}
                                    style={{
                                        width: spx(BASE.STAR_SIZE),
                                        height: spx(BASE.STAR_SIZE),
                                        border: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        padding: 0,
                                        display: "grid",
                                        placeItems: "center",
                                        opacity: 1,      // ✅ 기존 grayscale 대신 더 깔끔
                                    }}
                                    aria-label={`${n}점`}
                                >
                                    <StarIcon filled={filled} size={spx(120)} />  {/* ✅ 별 크기 */}
                                </button>
                            );
                        })}

                    </div>
                    <div style={{ fontSize: sfpx(28), color: "#666", textAlign: "center" }}>
                        {rating > 0 ? RATING_LABELS[rating] : "별점을 등록해주세요"}
                    </div>
                </div>

                {/* 4. 키워드 칩 */}
                <div style={{ display: "flex", flexDirection: "column", gap: spx(12) }}>
                    <div style={{ fontWeight: FW.bold, fontSize: sfpx(32), color: COLOR.text }}>
                        키워드 <span style={{ fontWeight: 400, fontSize: sfpx(24), color: "#999" }}>(최대 5개)</span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: spx(12) }}>
                        {reviewKeywords.map(kw => {
                            const sel = keywords.includes(kw.id);
                            return (
                                <button
                                    key={kw.id}
                                    type="button"
                                    onClick={() => toggleKeyword(kw.id)}
                                    style={{
                                        padding: `${spx(12)} ${spx(24)}`,
                                        fontSize: sfpx(28),
                                        fontWeight: FW.medium,
                                        borderRadius: RADIUS.full,
                                        border: sel ? BORDER.dark : "1px solid #ddd",
                                        background: sel ? COLOR.black : "#f5f5f5",
                                        color: sel ? COLOR.white : "#555",
                                        cursor: "pointer",
                                    }}
                                >
                                    {kw.keyword}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* 5. 등록 버튼 */}
                <div style={{ flex: 1 }} />
                <div style={{ display: "flex", justifyContent: "flex-end", paddingBottom: spx(40) }}>
                    <button
                        onClick={submit}
                        disabled={submitting}
                        style={{
                            width: spx(BASE.BTN_W),
                            height: spx(BASE.BTN_H),
                            fontSize: sfpx(40),
                            fontWeight: FW.extraBold,
                            borderRadius: RADIUS.md,
                            border: BORDER.dark,
                            background: submitting ? "#666" : COLOR.black,
                            color: COLOR.white,
                            cursor: submitting ? "not-allowed" : "pointer",
                        }}
                    >
                        {submitting ? "등록 중..." : "등록하기"}
                    </button>
                </div>
            </div>
        </PageShell>
    );
}
